<template>
  <header class="sticky-header">
    <EnvIndicator />
    <div class="header-container">
      <div class="header-content d-flex align-items-center position-relative">
        <nav class="navigation">
          <div
            class="d-flex"
            :class="{
              'gap-5': !canUseQuickFilterJobList,
              'align-items-center': canUseQuickFilterJobList
            }"
          >
            <router-link
              :to="{ name: 'home' }"
              class="d-flex flex-grow-1"
            >
              <img
                class="logo"
                :class="{
                  'logo-border': canUseQuickFilterJobList
                }"
                :src="logo"
                alt="Sealed"
              />
            </router-link>
            <div
              v-if="canUseQuickFilterJobList"
              class="contractor-name"
            >
              <h5>{{ contractor?.name }}</h5>
            </div>
            <div
              v-else
              class="desktop-search-button"
            >
              <Button
                v-if="currentAuthenticatedUser"
                type="button"
                variant="secondary-on-background"
                size="sm"
                :text="t('common_actions.search')"
                icon="search"
                @click="showOmniSearch = true"
              />
            </div>
          </div>

          <div
            v-if="currentAuthenticatedUser"
            class="right-nav"
          >
            <template
              v-for="item in navItems"
              :key="`${item.label}-menu-button`"
            >
              <Button
                v-if="item.onClick && item.label"
                type="button"
                :text="item.label"
                :icon="item.icon"
                @click="item.onClick"
                size="sm"
                variant="secondary-on-background"
              />
              <DropdownMenu
                v-else-if="item.menuItems"
                :menuItems="item.menuItems"
                :icon="item.icon"
                :label="item.label"
              />
            </template>
          </div>
          <div
            v-if="currentAuthenticatedUser"
            class="menu-container"
          >
            <IconButton
              v-if="!canUseQuickFilterJobList"
              variant="secondary-on-background"
              type="button"
              icon="search"
              @click="showOmniSearch = true"
            />
            <DropdownMenu
              icon="menu"
              :menuItems="navItems"
            />
          </div>
        </nav>
      </div>
    </div>
    <AnnouncementBanner
      v-if="
        announcement &&
        announcement.type === AnnouncementTypeType.BANNER &&
        !appStateStore.hiddenAnnouncements.includes(announcement.id)
      "
      :announcement="announcement"
      @dismiss="
        (announcementId) => appStateStore.hideAnnouncement(announcementId)
      "
    />
    <Teleport
      to="#modal-target"
      v-if="showOmniSearch"
    >
      <OmniSearch @close="showOmniSearch = false" />
    </Teleport>
    <AnnouncementModal
      v-if="
        announcement &&
        announcement.type === AnnouncementTypeType.POP_UP &&
        !appStateStore.hiddenAnnouncements.includes(announcement.id)
      "
      :announcement="announcement"
      @close="announcement = null"
      @dismiss="
        (announcementId) => appStateStore.hideAnnouncement(announcementId)
      "
    />
  </header>
</template>
<script setup lang="ts">
import logo from '@/assets/images/sealed-logo.svg';
import { useAppStore } from '@/store/appState';
import Button from '@component-module/components/Button/Button.vue';
import DropdownMenu, {
  type MenuItem
} from '@component-module/components/DropdownMenu/DropdownMenu.vue';
import IconButton from '@component-module/components/IconButton/IconButton.vue';
import AnnouncementBanner from '@shared/components/AnnouncementBanner.vue';
import AnnouncementModal from '@shared/components/AnnouncementModal.vue';
import EnvIndicator from '@shared/components/EnvIndicator.vue';
import OmniSearch from '@shared/components/OmniSearch.vue';
import {
  AccessTypeType,
  AnnouncementsRepository,
  type AnnouncementResponseType,
  AnnouncementTypeType
} from '@shared/generated';
import { useContractorStore } from '@shared/stores/contractor';
import { useUserStore } from '@shared/stores/user';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { storeToRefs } from 'pinia';
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

const canUseQuickFilterJobList = useLDFlag('quick-filter-job-list', false);
const canUseQuickQuotes = useLDFlag('quick-quotes', false);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const appStateStore = useAppStore();
const contractorStore = useContractorStore();
const userStore = useUserStore();
const { currentS3User, currentAuthenticatedUser } = storeToRefs(userStore);

const announcement = ref<AnnouncementResponseType | null>(null);
const showOmniSearch = ref(false);
const { contractor } = storeToRefs(contractorStore);

onMounted(async () => {
  // !route.query.token means "NOT homeowner user"
  if (Boolean(currentAuthenticatedUser.value) && !route.query.token) {
    await contractorStore.fetchCurrentContractor();
    announcement.value =
      await AnnouncementsRepository.announcementsGetCurrentAnnouncement({});
  }
});

const loggedUser = computed(() => {
  if (!contractor.value || !currentAuthenticatedUser.value) return null;
  return contractor.value.users.find(
    (u) => u.email === currentS3User.value?.email
  );
});

const navItems = computed(() => {
  const items: MenuItem[] = canUseQuickFilterJobList.value
    ? [
        {
          icon: 'add',
          label: t('common_actions.add_new_job'),
          onClick: () => router.push({ name: 'JobCreate' })
        },
        {
          icon: 'view_list',
          label: t('jobs_list.nav_label'),
          onClick: () => router.push({ name: 'JobList' })
        },
        {
          icon: 'home_repair_service',
          label: t('common_equipment.equipment'),
          onClick: () => router.push({ name: 'Equipment' })
        }
      ]
    : [
        {
          icon: 'view_list',
          label: t('jobs_list.nav_label'),
          onClick: () => router.push({ name: 'JobList' })
        },
        {
          icon: 'home_repair_service',
          label: t('common_equipment.equipment'),
          onClick: () => router.push({ name: 'Equipment' })
        }
      ];
  if (canUseQuickQuotes.value) {
    items.push({
      icon: 'bolt',
      label: 'Quick quote',
      onClick: () => router.push({ name: 'QuickQuote' })
    });
  }
  if (contractor.value) {
    const accountItems: MenuItem[] = [
      {
        label: t('common_actions.sign_out'),
        onClick: () => router.push({ name: 'logout' })
      }
    ];
    if (currentS3User.value?.access.type === AccessTypeType.ADMIN) {
      accountItems.unshift({
        label: t('common_actions.company_profile'),
        onClick: () => router.push({ name: 'CompanyProfile' })
      });
    }
    accountItems.unshift({
      label: t('common_actions.user_preferences'),
      onClick: () => router.push({ name: 'UserPreferences' })
    });
    items.push({
      icon: 'account_circle',
      label: loggedUser.value
        ? `${loggedUser.value.first_name} ${loggedUser.value.last_name}`
        : contractor.value.name,
      menuItems: accountItems
    });
  }
  return items;
});

window.addEventListener('keydown', (e) => {
  if (e.metaKey && e.key === 'k') {
    e.preventDefault();
    showOmniSearch.value = true;
  }
});
</script>

<style lang="scss" scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 1.5rem;
}

.header-container {
  padding: 1.5rem 0;
  @media screen and (min-width: $breakpoint-lg) {
    padding: 2rem 0;
  }
}

.header-content {
  padding: 0 1.75rem;
}

.logo {
  position: relative;
  height: 20px;
  @media screen and (min-width: $breakpoint-lg) {
    width: 133px;
    height: 40px;
  }
}

.logo-border {
  @media screen and (min-width: $breakpoint-lg) {
    border-right: 1px solid $white;
    padding-right: $spacing-md;
  }
}

.contractor-name {
  display: none;
  @media screen and (min-width: $breakpoint-lg) {
    display: block;
    padding: 0 $spacing-md;
    color: $white;
    h5 {
      margin: 0;
      font-weight: 400;
    }
  }
}

.navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  .right-nav,
  .desktop-search-button {
    display: none;
    @media screen and (min-width: $breakpoint-lg) {
      display: flex;
      gap: 1rem;
    }
  }
  .menu-container {
    display: flex;
    gap: 1rem;
    @media screen and (min-width: $breakpoint-lg) {
      display: none;
    }
  }
}
</style>
